import React, { useState, useEffect } from 'react';
import { Headphones, Play, Download, Clock, Database } from 'lucide-react';
import { recordingsService } from 'api/services/recordings.service';
import { organizationsService } from 'api/services/organizations.service';
import { Recording, PaginatedResponse } from 'types';
import { useApi } from 'hooks/useApi';
import { useAuth } from 'hooks/useAuth';
import { LoadingSpinner } from 'components/Common/LoadingSpinner';
import { ErrorMessage } from 'components/Common/ErrorMessage';
import Pagination from 'components/Common/Pagination';
import OrganizationFilter from 'components/Filters/OrganizationFilter';
import { formatDate, formatBytes, formatDuration, isOrganizationScopeError } from 'utils/helpers';
import { ITEMS_PER_PAGE, FEATURE_FLAGS } from 'utils/constants';

const Recordings: React.FC = () => {
  const { user, currentOrganization } = useAuth();
  const currentRole = (currentOrganization?.role || user?.role)?.toLowerCase();

  const [currentPage, setCurrentPage] = useState(1);
  const [downloading, setDownloading] = useState<number | null>(null);
  const [playing, setPlaying] = useState<number | null>(null);
  // Feature Flag: REQUIRE_ORG_SELECTION_FOR_OPERATIONAL_DATA
  // When enabled, defaults to current organization ID, otherwise 'all'
  const [selectedOrgId, setSelectedOrgId] = useState<string | 'all'>(
    FEATURE_FLAGS.REQUIRE_ORG_SELECTION_FOR_OPERATIONAL_DATA
      ? (currentOrganization?.id || '')
      : 'all'
  );

  // Determine organizationId to pass to API
  const getOrganizationId = () => {
    if (currentRole === 'master_admin' || currentRole === 'super_admin' || currentRole === 'service_admin') {
      // When REQUIRE_ORG_SELECTION is enabled, selectedOrgId should never be 'all' or empty
      // Return undefined for 'all' or empty string to prevent invalid API calls
      if (selectedOrgId === 'all' || !selectedOrgId) {
        return undefined;
      }
      return selectedOrgId;
    }
    // For other roles, only return valid organization IDs (not synthetic)
    if (currentOrganization?.id && currentOrganization.id !== 'synthetic-org') {
      return currentOrganization.id;
    }
    return undefined;
  };

  // Check if organization selection is required but not selected
  // service_admin belongs here with the other two. getOrganizationId() already
  // treats all three the same — it returns undefined until an organisation is
  // picked — but only master and super admins were ever ASKED to pick one. A
  // service provider therefore got no prompt and no organisationId, and the
  // backend's no-organisation path reads the LOCAL call tables rather than
  // federating to the switches, which is where this estate's calls actually
  // live. The page came back empty and said nothing.
  const needsOrgSelection = FEATURE_FLAGS.REQUIRE_ORG_SELECTION_FOR_OPERATIONAL_DATA &&
                            (currentRole === 'master_admin' || currentRole === 'super_admin' || currentRole === 'service_admin') &&
                            (!selectedOrgId || selectedOrgId === 'all');

  const { data, loading, error, refetch } = useApi<PaginatedResponse<Recording>>(
    () => {
      // Skip API call if organization selection is required but not selected
      if (needsOrgSelection) {
        return Promise.resolve({ success: true, data: { data: [], total: 0, totalPages: 0, currentPage: 1 } });
      }
      return recordingsService.getRecordings({
        page: currentPage,
        perPage: ITEMS_PER_PAGE,
        organizationId: getOrganizationId()
      });
    },
    [currentPage, selectedOrgId, currentOrganization?.id, needsOrgSelection]
  );

  const handleDownload = async (recording: Recording) => {
    setDownloading(recording.id);
    try {
      const blob = await recordingsService.downloadRecording('');
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `recording-${recording.id}.mp3`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Download failed:', error);
    } finally {
      setDownloading(null);
    }
  };

  const handlePlay = (recording: Recording) => {
    setPlaying(recording.id);
    try {
      // Get authenticated URL with token
      const authenticatedUrl = recordingsService.getAuthenticatedRecordingUrl(recording.recording_url);

      // Create and play audio element directly (streaming)
      const audio = new Audio(authenticatedUrl);
      audio.play();

      audio.onended = () => {
        setPlaying(null);
      };

      audio.onerror = () => {
        console.error('Audio playback failed');
        setPlaying(null);
      };
    } catch (error) {
      console.error('Play failed:', error);
      setPlaying(null);
    }
  };

  // Check if error is "Organization not found" - in this case, show error but keep filters visible
  // One matcher, in helpers — see isOrganizationScopeError for why this must
  // not be a literal comparison against the server's wording.
  const isOrgNotFoundError = isOrganizationScopeError(error);

  if (loading) return <LoadingSpinner />;
  // For "Organization not found" error, continue rendering to show organization filter
  if (error && !isOrgNotFoundError) return <ErrorMessage message={error} onRetry={refetch} />;
  if (!data && !isOrgNotFoundError) return null;

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold text-gray-800">
          Call Recordings {data ? `(${data.total})` : ''}
        </h1>
        {data && (
          <div className="flex gap-4">
            <div className="flex items-center gap-2 px-4 py-2 bg-gray-100 rounded-lg">
              <Database className="w-5 h-5 text-gray-600" />
              <span className="text-sm font-medium">
                Total Size: {formatBytes(
                  data.data.reduce((acc, r) => acc + r.file_size, 0)
                )}
              </span>
            </div>
          </div>
        )}
      </div>

      {/* Organization Filter - For master_admin, super_admin, and service_admin */}
      {(currentRole === 'master_admin' || currentRole === 'super_admin' || currentRole === 'service_admin') && (
        <div className="bg-white rounded-lg shadow p-4">
          <OrganizationFilter
            value={selectedOrgId === 'all' ? '' : selectedOrgId}
            onChange={(value) => {
              // Feature Flag: When REQUIRE_ORG_SELECTION is enabled, don't allow 'all'
              if (FEATURE_FLAGS.REQUIRE_ORG_SELECTION_FOR_OPERATIONAL_DATA) {
                setSelectedOrgId(value || selectedOrgId); // Keep current if empty
              } else {
                setSelectedOrgId(value || 'all'); // Legacy: allow 'all'
              }
              setCurrentPage(1);
            }}
            className="w-full md:w-96"
            requireSelection={FEATURE_FLAGS.REQUIRE_ORG_SELECTION_FOR_OPERATIONAL_DATA}
            allowServiceProviderSelection={true}
          />
        </div>
      )}

      {/* Show error message if organization not found, but keep filters visible */}
      {isOrgNotFoundError && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center gap-2">
            <div className="flex-shrink-0">
              <svg className="w-5 h-5 text-red-600" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="flex-1">
              <h3 className="text-sm font-medium text-red-800">Organization Not Found</h3>
              <p className="mt-1 text-sm text-red-700">{error}</p>
              <p className="mt-1 text-sm text-red-600">Please select a different organization from the dropdown above.</p>
            </div>
          </div>
        </div>
      )}

      {!isOrgNotFoundError && data && <div className="bg-white rounded-lg shadow">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Recording ID
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Call ID
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Session
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Duration
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  File Size
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Created At
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {data.data.map((recording) => (
                <tr key={recording.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 text-sm text-gray-900">
                    REC-{recording.id}
                  </td>
                  <td className="px-6 py-4 text-sm text-blue-600 hover:underline cursor-pointer">
                    CALL-{recording.call_log_id}
                  </td>
                  <td className="px-6 py-4 text-sm text-blue-600 hover:underline cursor-pointer">
                    SES-{recording.session_id}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-900">
                    <div className="flex items-center gap-1">
                      <Clock className="w-4 h-4 text-gray-500" />
                      {formatDuration(recording.duration_ms)}
                    </div>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-900">
                    {formatBytes(recording.file_size)}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-900">
                    {formatDate(recording.created_at)}
                  </td>
                  <td className="px-6 py-4 text-sm">
                    <div className="flex gap-2">
                      <button
                        onClick={() => handlePlay(recording)}
                        disabled={playing === recording.id}
                        className="p-2 text-green-600 hover:bg-green-50 rounded disabled:opacity-50"
                        title="Play Recording"
                      >
                        {playing === recording.id ? (
                          <div className="w-4 h-4 animate-spin rounded-full border-2 border-green-600 border-t-transparent" />
                        ) : (
                          <Play className="w-4 h-4" />
                        )}
                      </button>
                      <button
                        onClick={() => handleDownload(recording)}
                        disabled={downloading === recording.id}
                        className="p-2 text-gray-600 hover:bg-gray-100 rounded disabled:opacity-50"
                        title="Download Recording"
                      >
                        {downloading === recording.id ? (
                          <div className="w-4 h-4 animate-spin rounded-full border-2 border-gray-600 border-t-transparent" />
                        ) : (
                          <Download className="w-4 h-4" />
                        )}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        
        <div className="p-4 border-t">
          <Pagination
            currentPage={currentPage}
            totalPages={data.totalPages}
            onPageChange={setCurrentPage}
            totalItems={data.total}
            itemsPerPage={ITEMS_PER_PAGE}
          />
        </div>
      </div>}
    </div>
  );
};

export default Recordings;